/* eslint-disable no-underscore-dangle */
import { logError } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { retrievePlatformId } from './productClient';
import { LinkInputType, LinkResponseType, LinkType } from 'src/types/link';
import { APIError } from 'src/types/index';
import { RenderingSDK } from '@rendering/web-client';

/**
 * The values needed for linking an asset to a product
 * @typedef {Object} LinkInput
 * @property {string} ReferenceId A product sku to associate an asset with
 * @property {string[]} [SubReferenceIds] List of surfaces of the product that the asset is associated with
 * @property {Object} [Attributes] Product variables for the sku
 * @property {string} Purpose - The purpose of the asset (design, product, merchandising)
 * @property {string} SubPurpose The sub purpose of the asset. For design scenes sub purposes include underlay and overlay
 * @property {string[]} [Tags] List of tags for the asset
 */

/**
 * Links a scene to a product.
 * @param options
 * @param options.assetId - The ID of the asset being linked
 * @param options.linkInput - The object containing link properties.
 * @returns - The promise of the request.
 */
export function createLink(sdk: RenderingSDK, { assetId, linkInput }: { assetId: string; linkInput: LinkInputType }) {
    return sdk.AssetLinks.create(assetId, linkInput).catch((e) => {
        logError(`create asset link failed! ${parseError(e)} ${assetId} ${JSON.stringify(linkInput)}`);
        throw e;
    });
}

export function deleteLink(sdk: RenderingSDK, { assetId, linkId }: { assetId: string; linkId: string }) {
    return sdk.AssetLinks.remove(assetId, linkId).catch((e) => {
        logError(`delete asset link failed! ${parseError(e)} ${assetId} ${linkId}`);
        throw e;
    });
}

/**
 *  Links a scene to a product.
 * @param options
 * @param options.linkId - The existing link id.
 * @param options.assetId - The ID of the asset
 * @param options.linkInput - The object containing link properties.
 * @returns - The promise of the request.
 */
export function updateLink(
    sdk: RenderingSDK,
    {
        linkInput,
        assetId,
        linkId,
    }: {
        linkId: string;
        linkInput: LinkInputType;
        assetId: string;
    },
) {
    return sdk.AssetLinks.update(assetId, linkId, linkInput).catch((e) => {
        logError(`update asset link failed! ${parseError(e)} ${assetId} ${linkId} ${JSON.stringify(linkInput)}`);
        throw e;
    });
}

function mapLink(sdk: RenderingSDK, link: LinkResponseType): Promise<LinkType> {
    const output: LinkType = {
        id: link.id,
        referenceId: link.referenceId,
        referenceIdVersion: link.referenceIdVersion,
        sceneId: link.assetId,
        linkId: link.id,
        sku: link.referenceId,
        skuVersion: link.referenceIdVersion || 0,
        merchantSku: link.referenceId,
        merchantSkuVersion: link.referenceIdVersion || 0,
        merchantVariables: link.attributes || {},
        variableConfigurationId: link.variableConfigurationId,
        purpose: link.purpose,
        linkPurposes: link.linkPurposes,
        tenant: link.tenant,
        subPurpose: link.subPurpose,
        variables: link.attributes || {},
        tags: link.tags || [],
        surfaces: link.subReferenceIds ? link.subReferenceIds.map((id) => ({ id })) : [],
        view: link?.view,
        _links: link._links,
    };

    if (link.referenceId && link.referenceId.startsWith('PRD')) {
        try {
            return retrievePlatformId(sdk, {
                sku: link.referenceId,
                skuVersion: link.referenceIdVersion,
                attributes: link.attributes,
            }).then(({ merchantSku, merchantVersion, merchantVariables }) => {
                output.merchantSku = merchantSku;
                output.merchantSkuVersion = merchantVersion;
                output.merchantVariables = merchantVariables;
                return output;
            });
        } catch (error) {
            logError(`Failed to retrieve the VP platform id: ${link.referenceId} ${error}`);
            throw error;
        }
    }

    return Promise.resolve(output);
}

function mapLinks(sdk: RenderingSDK, results: LinkResponseType[]) {
    return Promise.all(results.map((link) => mapLink(sdk, link)));
}

/**
 * Gets all links for an asset
 * @param options
 * @param options.assetId The ID of the asset
 * @returns - The promise of the request.
 */
export function getLinksForAsset(sdk: RenderingSDK, { assetId }: { assetId: string }) {
    return sdk.AssetLinks.getLinksByAssetId(assetId, 100, undefined, { 'Cache-Control': 'no-cache' })
        .then((res) => {
            if (res._embedded && res._embedded.item) {
                return mapLinks(sdk, res._embedded.item);
            }
            return [];
        })
        .catch((e) => {
            logError(`query asset error (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Query for links based on sku
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param options.sku - The sku to query for.
 * @returns - The promise of the request.
 */
export function queryLinks(
    sdk: RenderingSDK,
    {
        tenantType,
        tenantId,
        sku,
        variables,
        purpose,
    }: {
        tenantType: string;
        tenantId: string;
        sku: string;
        variables?: Record<string, any> | undefined;
        purpose?: string | undefined;
    },
) {
    const data = {
        referenceId: sku,
        tenant: {
            id: tenantId,
            type: tenantType,
        },
        attributes: variables,
        purpose: purpose,
    };
    const options = {
        pageSize: '200',
    };
    return sdk.AssetLinks.query(data, options)
        .then((res) =>
            res._embedded.item?.filter((link) => link.tenant.id === tenantId && link.tenant.type === tenantType),
        )
        .then((res) => (res ? mapLinks(sdk, res) : []))
        .catch((e) => {
            logError(`Query links error (data=${JSON.stringify(data)}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Gets all settings for tenant
 * @param tenantType
 * @param tenantId
 * @returns - The promise of the request.
 */
export async function getPurposeSettings(sdk: RenderingSDK, tenantType: string, tenantId: string) {
    try {
        const res = await sdk.Settings.getSettings('assetLinkingSettings', tenantType, tenantId);
        if (res._embedded?.item?.length) {
            const { subPurposes, tags } = res._embedded.item[0].settings.settings;
            return { subPurposes, tags };
        }
        return [];
    } catch (error: unknown) {
        logError(
            `query asset error (tenantType=${tenantType}, tenantId=${tenantId}): ${parseError(error as APIError)}`,
        );
        throw error;
    }
}
